import React, { useEffect, useMemo, useState } from "react";

const FORM_ID = 1; // 🔁 Replace with your Gravity Form ID
const FORM_ENDPOINT = `https://drmohans.com/backend/wp-json/gf/v2/forms/${FORM_ID}/submissions`;

const LOCATIONS = [
  "Gopalapuram - Chennai",
  "Annanagar - Chennai",
  "Annanagar - Chennai",
  "Avadi - Chennai",
  "Tambaram - Chennai",
  "Karapakkam - Chennai",
  "Velachery - Chennai",
  "Porur - Chennai",
  "Selaiyur - Chennai",
  "Chunampet",
  "Kancheepuram",
  "Coimbatore",
  "Erode",
  "Gudiyatham",
  "Madurai",
  "Salem",
  "Thanjavur",
  "Tiruchirappalli",
  "Tuticorin",
  "Vellore",
  "Tirupur",
  "Pondicherry",
  "Domalguda - Hyderabad",
  "Jubilee Hills - Hyderabad",
  "Kukatpally - Hyderabad",
  "Dilsukh Nagar - Hyderabad",
  "Secunderabad - Hyderabad",
  "A.S.Rao Nagar - Hyderabad",
  "Nizamabad-Telangana",
  "Warangal-Telangana",
  "Vijayawada - Andhra Pradesh",
  "Visakhapatnam - Andhra Pradesh",
  "Rajahmundry - Andhra Pradesh",
  "Kadapa - Andhra Pradesh",
  "Kakinada - Andhra Pradesh",
  "Nellore - Andhra Pradesh",
  "Guntur - Andhra Pradesh",
  "Chittoor - Andhra Pradesh",
  "Tirupati - Andhra Pradesh",
  "Kurnool - Andhra Pradesh",
  "Bhubaneswar - Odisha",
  "Indira Nagar - Bengaluru",
  "JP Nagar - Bengaluru",
  "Malleshwaram - Bengaluru",
  "Whitefield - Bengaluru",
  "Basavanagudi - Bengaluru",
  "Trivandrum-Kerala",
  "Calicut-Kerala",
  "Kaikhali - Kolkata",
  "Rash Behari - Kolkata",
  "Siliguri - WestBengal",
];

const ContactFormPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    location: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");

  useEffect(() => {
    const popupInfo = localStorage.getItem("ContactFormSubmitted");
    if (popupInfo) {
      const { timestamp } = JSON.parse(popupInfo);
      const oneDay = 24 * 60 * 60 * 1000;
      const now = new Date().getTime();

      if (now - timestamp < oneDay) {
        return;
      }
    }

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        return /^\S+@\S+\.\S+$/.test(value)
          ? ""
          : "Please enter a valid email address.";
      case "phone":
        return /^[0-9]{10}$/.test(value)
          ? ""
          : "Please enter a valid phone number (digits only).";
      case "name":
        return value.trim() === "" ? "Name is required" : "";
      case "location":
        return value.trim() === "" ? "Location is required" : "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Live validation on change
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.values(newErrors)?.filter(Boolean)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      ["input_1.3"]: formData.name,
      ["input_2"]: formData.email,
      ["input_4"]: formData.phone,
      ["input_3"]: formData.message,
      ["input_7"]: formData.location,
    };

    if (!formData.name || !formData.email || !formData.phone) {
      setIsSubmitting(false);
      alert("Please fill all required fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.is_valid) {
        setIsSubmitting(false);
        localStorage.setItem(
          "ContactFormSubmitted",
          JSON.stringify({ submitted: true, timestamp: new Date().getTime() })
        );

        setThankYouMessage("Thank you! Your message has been submitted.");
        setTimeout(() => setShowPopup(false), 2000); // auto close after 2s
      } else {
        setIsSubmitting(false);
        alert("Submission failed. Please check your input.");
      }
    } catch (err) {
      setIsSubmitting(false);
      alert("Error submitting form.");
    }
  };

  const hasError = useMemo(
    () => Object.values(errors)?.filter(Boolean)?.length > 0,
    [errors]
  );

  if (!showPopup) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 transition-opacity duration-300"
      onClick={() => (isSubmitting ? null : setShowPopup(false))}
    >
      <div
        className="shadow-xl w-full max-w-md scale-100 transform rounded-2xl bg-white p-6 opacity-100 transition duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-openSans text-xl font-bold">Contact Us</h2>
          <button
            className="text-xl text-gray-500 hover:text-red-500"
            onClick={() => (isSubmitting ? null : setShowPopup(false))}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p
              className={`mb-2 font-openSans text-sm ${
                errors.name ? "text-red-500" : ""
              }`}
            >
              {errors.name || "Name*"}
            </p>
            <input
              className="w-full rounded border p-2 font-openSans hover:border-secondary focus:border-secondary focus:outline-none  focus-visible:border-secondary "
              placeholder="Name*"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div>
            <p
              className={`mb-2 font-openSans text-sm ${
                errors.email ? "text-red-500" : ""
              }`}
            >
              {errors.email || "Email*"}
            </p>
            <input
              className="w-full rounded border p-2 font-openSans hover:border-secondary focus:border-secondary focus:outline-none  focus-visible:border-secondary "
              placeholder="Email*"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div>
            <p
              className={`mb-2 font-openSans text-sm ${
                errors.phone ? "text-red-500" : ""
              }`}
            >
              {errors.phone || "Phone*"}
            </p>
            <input
              className="w-full rounded border p-2 font-openSans hover:border-secondary focus:border-secondary focus:outline-none  focus-visible:border-secondary "
              placeholder="Phone*"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, ""); // remove non-digits
                if (val.length <= 10) {
                  setFormData((prev) => ({ ...prev, phone: val }));
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    phone: validateField("phone", val),
                  }));
                }
              }}
              pattern="[0-9]*"
              maxLength={10}
            />
          </div>

          <div>
            <p
              className={`mb-2 font-openSans text-sm ${
                errors.location ? "text-red-500" : ""
              }`}
            >
              {errors.location || "Location*"}
            </p>
            <select
              name="location"
              // value={formData.location}
              onChange={handleChange}
              className="w-full rounded border bg-white p-2 font-openSans hover:border-secondary focus:border-secondary focus:outline-none  focus-visible:border-secondary "
            >
              <option value="">Choose location*</option>
              {LOCATIONS.map((location) => (
                <option
                  key={location}
                  value={location}
                  selected={location === formData.location}
                >
                  {location}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="mb-2 font-openSans text-sm">Message</p>
            <textarea
              className="w-full rounded border p-2 font-openSans hover:border-secondary focus:border-secondary focus:outline-none  focus-visible:border-secondary "
              placeholder="Message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          <button
            type="submit"
            className={`w-full rounded bg-primary p-2 font-openSans text-white transition hover:bg-secondary ${
              hasError || isSubmitting ? "opacity-70" : "opacity-100"
            }`}
            disabled={hasError || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>

          {thankYouMessage && (
            <p className="mt-2 mb-0 text-center font-openSans text-sm text-green-600">
              {thankYouMessage}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactFormPopup;
